import type {
  BillingBudget,
  BillingBudgetField,
  BillingMonthlyBudgetField,
  GcpBudgetUpsertRequest,
} from '../types/billing';
import {
  BILLING_EDITABLE_FIELDS,
  BILLING_MONTHLY_BUDGET_FIELDS,
  BILLING_NUMERIC_FIELDS,
} from '../types/billing';
import {
  applyAnnualBudgetRedistribution,
  applyManualMonthlyBudgetChange,
  type BillingBudgetValidationError,
  describeAdjustedMonths,
  getCalendarMonthBudgetIndex,
} from './billingBudgetValidation';

export type BillingFieldUpdateResult =
  | { success: true; row: BillingBudget; infoMessage?: string }
  | { success: false; row: BillingBudget; error: BillingBudgetValidationError };

function isMonthlyBudgetField(field: BillingBudgetField): field is BillingMonthlyBudgetField {
  return (BILLING_MONTHLY_BUDGET_FIELDS as readonly string[]).includes(field);
}

export function applyBillingFieldUpdate(
  base: BillingBudget,
  field: BillingBudgetField,
  raw: string,
): BillingFieldUpdateResult {
  const value = parseBillingFieldValue(field, raw);
  if (isMonthlyBudgetField(field)) {
    const numericValue = typeof value === 'number' ? value : null;
    const result = applyManualMonthlyBudgetChange(base, field, numericValue);
    if (!result.ok) {
      return { success: false, row: base, error: result.error };
    }
    const infoMessage =
      describeAdjustedMonths(result.adjustedFutureMonths, result.revisedRedistribution) ??
      undefined;
    return { success: true, row: result.row, infoMessage };
  }

  const updated: BillingBudget = { ...base, [field]: value };
  if (field === 'annual_fixed_budget' && value !== null) {
    // Editing the annual budget leaves past months untouched and redistributes the
    // current month → December from (annual budget − prior months − YTD overspend).
    const result = applyAnnualBudgetRedistribution(updated, getCalendarMonthBudgetIndex());
    if (!result.ok) {
      return { success: false, row: base, error: result.error };
    }
    const infoMessage =
      describeAdjustedMonths(result.adjustedFutureMonths, result.revisedRedistribution) ??
      undefined;
    return { success: true, row: result.row, infoMessage };
  }
  return { success: true, row: updated };
}

export function formatBillingNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(value);
}

export function parseBillingFieldValue(
  field: BillingBudgetField,
  raw: string,
): string | number | boolean | null {
  if (field === 'is_flagged') {
    const lower = raw.toLowerCase().trim();
    return lower === 'true' || lower === 'yes' || lower === 'y' || lower === '1';
  }
  if (BILLING_NUMERIC_FIELDS.includes(field)) {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return Number.isNaN(n) ? null : n;
  }
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

export function billingValuesEqual(
  a: BillingBudget[keyof BillingBudget],
  b: BillingBudget[keyof BillingBudget],
): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === 'number' && typeof b === 'number') {
    return Math.abs(a - b) < 1e-9;
  }
  return String(a) === String(b);
}

export function buildBillingUpdatePayload(
  original: BillingBudget,
  edited: BillingBudget,
): Partial<BillingBudget> {
  const payload: Partial<BillingBudget> = {};
  for (const field of BILLING_EDITABLE_FIELDS) {
    if (!billingValuesEqual(original[field], edited[field])) {
      (payload as Record<string, unknown>)[field] = edited[field];
    }
  }
  return payload;
}

export function cloneBillingRows(rows: BillingBudget[]): BillingBudget[] {
  return rows.map((r) => ({ ...r }));
}

export function isPastMonthlyBudgetField(field: BillingBudgetField): boolean {
  const index = (BILLING_MONTHLY_BUDGET_FIELDS as readonly string[]).indexOf(field);
  if (index < 0) {
    return false;
  }
  return index < getCalendarMonthBudgetIndex();
}

export function isEditableBillingField(field: BillingBudgetField): boolean {
  return (
    (BILLING_EDITABLE_FIELDS as readonly string[]).includes(field) &&
    !isPastMonthlyBudgetField(field)
  );
}

export function getEditableBillingFields(): Set<BillingBudgetField> {
  return new Set(BILLING_EDITABLE_FIELDS.filter((field) => !isPastMonthlyBudgetField(field)));
}

export function getCurrentMonthBudgetField(): BillingMonthlyBudgetField {
  return BILLING_MONTHLY_BUDGET_FIELDS[getCalendarMonthBudgetIndex()];
}

export function currentMonthBudgetChanged(
  original: BillingBudget,
  edited: BillingBudget,
): boolean {
  const field = getCurrentMonthBudgetField();
  return !billingValuesEqual(original[field], edited[field]);
}

export type GcpBudgetUpsertFailureReason = 'missing_budget' | 'invalid_budget' | 'missing_currency';

export function getGcpBudgetUpsertFailureReason(
  row: BillingBudget,
): GcpBudgetUpsertFailureReason | null {
  const field = getCurrentMonthBudgetField();
  const amount = row[field];
  if (amount === null || amount === undefined) {
    return 'missing_budget';
  }
  if (amount <= 0) {
    return 'invalid_budget';
  }
  const currency = row.currency?.trim();
  if (!currency) {
    return 'missing_currency';
  }
  return null;
}

export function buildGcpBudgetUpsertRequest(
  row: BillingBudget,
): GcpBudgetUpsertRequest | null {
  if (getGcpBudgetUpsertFailureReason(row)) {
    return null;
  }
  const field = getCurrentMonthBudgetField();
  return {
    billing_account_id: row.account_id,
    budget_amount: row[field] as number,
    currency_code: row.currency!.trim(),
  };
}
